import React from 'react';
import { ArrowUpDown, Filter, Search, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { GENDER_OPTIONS, type Gender, type SortKey } from '../../types/student';
const ALL = 'all';
export interface StudentFilters {
  search: string;
  sort: SortKey;
  gender: Gender;
  className: string;
}
interface StudentToolbarProps {
  filters: StudentFilters;
  onChange: (filters: StudentFilters) => void;
  classes: string[];
  resultCount: number;
  totalCount: number;
}
export function StudentToolbar({
  filters,
  onChange,
  classes,
  resultCount,
  totalCount
}: StudentToolbarProps) {
  const hasFilters = Boolean(filters.search || filters.gender || filters.className) || filters.sort !== 'newest';
  return <div className="flex flex-col gap-3 border-b border-[var(--glass-hairline)] p-4 lg:flex-row lg:items-center" data-id="element-439" data-mp="rnt2u5td010j">
      <div className="relative flex-1" data-id="element-440" data-mp="24uza7mz20mhl">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" data-id="element-441" data-mp="24txw19mqrjvn" />
        <Input value={filters.search} onChange={event => onChange({
        ...filters,
        search: event.target.value
      })} placeholder="Search names, class, parents or telephone…" aria-label="Search student records" className="h-10 rounded-xl pl-9" data-id="element-442" data-mp="1zvmpb7rqtphn" />
        {filters.search && <button type="button" onClick={() => onChange({
        ...filters,
        search: ''
      })} aria-label="Clear search" className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-muted-foreground hover:text-foreground" data-id="element-443" data-mp="1h5er942whp8b">
            <X className="h-3.5 w-3.5" data-id="element-444" data-mp="12g8vxr76oj5p" />
          </button>}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-id="element-445" data-mp="12e5umb0kz3lo">
        <Select value={filters.className || ALL} onValueChange={value => onChange({
        ...filters,
        className: value === ALL ? '' : value
      })} data-id="element-446" data-mp="xgnvfccd82qv">
          <SelectTrigger className="h-10 w-[150px] rounded-xl" aria-label="Filter by class" data-id="element-447" data-mp="32i8gbsczkgjh">
            <Filter className="mr-1.5 h-3.5 w-3.5 opacity-60" data-id="element-448" data-mp="378yiyxu125ah" />
            <SelectValue placeholder="All classes" data-id="element-449" data-mp="37bkxxij8zn2q" />
          </SelectTrigger>
          <SelectContent data-id="element-450" data-mp="3c81qcoxhcnlm">
            <SelectItem value={ALL} data-id="element-451" data-mp="3cae2glgk43qr">All classes</SelectItem>
            {classes.map(className => <SelectItem key={className} value={className} data-id="element-452" data-mp="2jjw2vuf1kki3">
                {className}
              </SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filters.gender || ALL} onValueChange={value => onChange({
        ...filters,
        gender: (value === ALL ? '' : value) as Gender
      })} data-id="element-453" data-mp="2ohp6cfoyn0eo">
          <SelectTrigger className="h-10 w-[140px] rounded-xl" aria-label="Filter by gender" data-id="element-454" data-mp="3rztg87thoom">
            <SelectValue placeholder="All genders" data-id="element-455" data-mp="7tbowuddh44q" />
          </SelectTrigger>
          <SelectContent data-id="element-456" data-mp="1scx7ybp6zhs9">
            <SelectItem value={ALL} data-id="element-457" data-mp="1sb41oxihqzks">All genders</SelectItem>
            {GENDER_OPTIONS.map(gender => <SelectItem key={gender} value={gender} data-id="element-458" data-mp="1obc1p975y1r1">
                {gender}
              </SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filters.sort} onValueChange={value => onChange({
        ...filters,
        sort: value as SortKey
      })} data-id="element-459" data-mp="1eliqi9axnonb">
          <SelectTrigger className="h-10 w-[170px] rounded-xl" aria-label="Sort records" data-id="element-460" data-mp="19mz0nxt7xspo">
            <ArrowUpDown className="mr-1.5 h-3.5 w-3.5 opacity-60" data-id="element-461" data-mp="14mjoh9koar9a" />
            <SelectValue data-id="element-462" data-mp="10l7yh2xw040k" />
          </SelectTrigger>
          <SelectContent data-id="element-463" data-mp="10m0yll2cbv9q">
            <SelectItem value="newest" data-id="element-464" data-mp="10nc17hk1uwz9">Newest first</SelectItem>
            <SelectItem value="oldest" data-id="element-465" data-mp="vng667tbyfvu">Oldest first</SelectItem>
            <SelectItem value="name-asc" data-id="element-466" data-mp="2uyz2nmxg5vom">Name A–Z</SelectItem>
            <SelectItem value="name-desc" data-id="element-467" data-mp="2uyh7yuqfnpit">Name Z–A</SelectItem>
            <SelectItem value="class-asc" data-id="element-468" data-mp="2q17caxcgahmj">Class A–Z</SelectItem>
          </SelectContent>
        </Select>

        {hasFilters && <Button variant="ghost" className="h-10 rounded-xl text-muted-foreground" onClick={() => onChange({
        search: '',
        sort: 'newest',
        gender: '',
        className: ''
      })} data-id="element-469" data-mp="1mq1juu3heo9s">
            Reset
          </Button>}

        <p className="ml-auto whitespace-nowrap text-xs text-muted-foreground lg:ml-2" data-id="element-470" data-mp="1imlri9yg3a2w">
          <span className="font-mono font-semibold text-foreground" data-id="element-471" data-mp="13yikcx8o1sci">{resultCount}</span> of{' '}
          <span className="font-mono" data-id="element-472" data-mp="18q0izen7vgub">{totalCount}</span>
        </p>
      </div>
    </div>;
}